import { IS_NATIVE, platform } from '@/platform';
import { logger } from '@/lib/logger';
import { SUPABASE_AUTH_KEY } from '@/integrations/supabase/client';
import { authStorage } from '@/platform/authStorage';
import { onSessionWipe } from '@/lib/sessionWipe';

/**
 * The client's bookings as last read from the server, kept in the app so My
 * Bookings stays readable without a connection (M10).
 *
 * App only, in secure storage (Keystore-encrypted): the list carries amounts
 * and private addresses (assessment §R P1-6). Sign-out clears secure storage,
 * and a snapshot is only ever shown to the account that saved it, so one that
 * a sign-out raced past is never another person's to read. What is kept is a
 * projection: no mobile-money number, no coordinates, nothing a read-only
 * list does not show. The website keeps nothing.
 */

export const OFFLINE_BOOKINGS_KEY = 'offline.my-bookings';

// Sign-out fence (Codex on #1134). Each sign-out moves the generation on and
// waits for the writes already started; a load that began before it carries
// the old generation and writes nothing, so no list is put back after
// secure storage is cleared.
let generation = 0;
let writes: Promise<void> = Promise.resolve();

/** Taken when a load starts, and handed to saveOfflineBookings with its result. */
export function offlineBookingsGeneration(): number {
  return generation;
}

/** Sign-out: no load from before this point may write, and none still writing outlives it. */
export async function forgetOfflineBookings(): Promise<void> {
  generation += 1;
  await writes;
  // Its own key too, not only the bulk clear after it (Codex on #1134): a
  // clear that fails is logged and sign-out goes on, and the list would then
  // outlive the session.
  try {
    await platform.storage.secure.remove(OFFLINE_BOOKINGS_KEY);
  } catch (error) {
    logger.warn('[offline-bookings] could not remove the kept bookings list', error);
  }
}

// Every end of a session, idle expiry and the app lock's included.
onSessionWipe(forgetOfflineBookings);

export interface OfflineBookingRow {
  id: string;
  booking_reference: string | null;
  status: string | null;
  payment_status: string | null;
  service_name: string | null;
  service_category: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  total_amount: number | null;
  currency_code: string | null;
  client_address: string | null;
  helper_name: string | null;
}

export interface OfflineBookingsSnapshot {
  v: 1;
  userId: string;
  /** When the server answered: what the list is "as of". */
  savedAt: string;
  rows: OfflineBookingRow[];
}

interface SourceRow {
  id: string;
  booking_reference?: string | null;
  status?: string | null;
  payment_status?: string | null;
  service_name?: string | null;
  service_category?: string | null;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  total_amount?: number | null;
  currency_code?: string | null;
  client_address?: string | null;
  helper_profile?: { first_name?: string | null; last_name?: string | null } | null;
}

/** Only the fields the read-only list shows. */
export function offlineProjection(row: SourceRow): OfflineBookingRow {
  const first = row.helper_profile?.first_name?.trim() ?? '';
  const last = row.helper_profile?.last_name?.trim() ?? '';
  const helperName = [first, last ? `${last[0]}.` : ''].filter(Boolean).join(' ');
  return {
    id: row.id,
    booking_reference: row.booking_reference ?? null,
    status: row.status ?? null,
    payment_status: row.payment_status ?? null,
    service_name: row.service_name ?? null,
    service_category: row.service_category ?? null,
    scheduled_date: row.scheduled_date ?? null,
    scheduled_time: row.scheduled_time ?? null,
    total_amount: typeof row.total_amount === 'number' ? row.total_amount : null,
    currency_code: row.currency_code ?? null,
    client_address: row.client_address ?? null,
    helper_name: helperName || null,
  };
}

/**
 * The user of the session kept on this phone, read from the auth store
 * itself, never through getSession() (Codex on #1134): near expiry that
 * refreshes the token, and offline the refresh can stall, holding this write,
 * and the sign-out that waits for it, for good. Read through the adapter
 * auth-js uses, so a session the wipe removed, or hid after a failed
 * removal, reads as none.
 */
async function storedSessionUserId(): Promise<string | null> {
  const raw = await authStorage.getItem(SUPABASE_AUTH_KEY);
  if (!raw) return null;
  try {
    const user = (JSON.parse(raw) as { user?: { id?: unknown } } | null)?.user;
    return typeof user?.id === 'string' ? user.id : null;
  } catch {
    return null;
  }
}

/**
 * Keep the list the server just returned. App only; never throws. `since` is
 * the generation taken when the load began: a sign-out in between drops it.
 */
export async function saveOfflineBookings(
  userId: string,
  rows: SourceRow[],
  {
    since = generation,
    now = new Date(),
    addressesUnavailable = false,
  }: {
    since?: number;
    now?: Date;
    /** The private addresses could not be read this time: keep the ones already kept. */
    addressesUnavailable?: boolean;
  } = {},
): Promise<void> {
  if (!IS_NATIVE || !userId || since !== generation) return;
  const snapshot: OfflineBookingsSnapshot = {
    v: 1,
    userId,
    savedAt: now.toISOString(),
    rows: rows.map(offlineProjection),
  };
  writes = writes.then(async () => {
    if (since !== generation) return;
    try {
      // Still this person's session, read from storage at the moment of
      // writing (Codex on #1134): a load that began after a sign-out started
      // carries the new generation, so the generation alone cannot stop it.
      // The local-first wipe removes the auth keys before it clears the rest,
      // so from then on no session is found and nothing is written.
      if ((await storedSessionUserId()) !== userId) return;
      if (addressesUnavailable) {
        // A partial read must not overwrite a complete list (Codex on #1134):
        // the addresses kept before stand in for the ones that failed.
        const before = new Map((await loadOfflineBookings(userId))?.rows.map((r) => [r.id, r.client_address]) ?? []);
        snapshot.rows = snapshot.rows.map((r) => ({ ...r, client_address: before.get(r.id) ?? null }));
      }
      await platform.storage.secure.set(OFFLINE_BOOKINGS_KEY, JSON.stringify(snapshot));
    } catch (error) {
      logger.warn('[offline-bookings] could not keep the bookings list', error);
    }
  });
  await writes;
}

const text = (v: unknown): string | null => (typeof v === 'string' ? v : null);

// Only values the view's formatters accept (Codex on #1134): a string the
// date or currency formatter rejects would throw a RangeError and take the
// offline page down with it.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}(:\d{2})?$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
const date = (v: unknown): string | null =>
  typeof v === 'string' && DATE_RE.test(v) && !Number.isNaN(Date.parse(v)) ? v : null;
const time = (v: unknown): string | null => (typeof v === 'string' && TIME_RE.test(v) ? v : null);
const currency = (v: unknown): string | null => {
  if (typeof v !== 'string' || !CURRENCY_RE.test(v)) return null;
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency: v });
    return v;
  } catch {
    return null;
  }
};

/** A kept row as the view reads it, or null when it is not one. */
function readRow(v: unknown): OfflineBookingRow | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id) return null;
  return {
    id: r.id,
    booking_reference: text(r.booking_reference),
    status: text(r.status),
    payment_status: text(r.payment_status),
    service_name: text(r.service_name),
    service_category: text(r.service_category),
    scheduled_date: date(r.scheduled_date),
    scheduled_time: time(r.scheduled_time),
    total_amount: typeof r.total_amount === 'number' && Number.isFinite(r.total_amount) ? r.total_amount : null,
    currency_code: currency(r.currency_code),
    client_address: text(r.client_address),
    helper_name: text(r.helper_name),
  };
}

/** This account's kept list, or null: none, someone else's, or unreadable. App only. */
export async function loadOfflineBookings(userId: string): Promise<OfflineBookingsSnapshot | null> {
  if (!IS_NATIVE || !userId) return null;
  try {
    const raw = await platform.storage.secure.get(OFFLINE_BOOKINGS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OfflineBookingsSnapshot>;
    if (parsed?.v !== 1 || parsed.userId !== userId || !Array.isArray(parsed.rows) || typeof parsed.savedAt !== 'string') {
      return null;
    }
    // Row by row too (Codex on #1134): one malformed row must not break the
    // page that exists to work when nothing else does.
    const rows = parsed.rows.flatMap((r) => {
      const row = readRow(r);
      return row ? [row] : [];
    });
    // An unreadable savedAt is shown as "when last loaded" (OfflineBookingsView).
    return { v: 1, userId, savedAt: parsed.savedAt, rows };
  } catch (error) {
    logger.warn('[offline-bookings] could not read the kept bookings list', error);
    return null;
  }
}
